import { CSSProperties, FC, useEffect, useRef, useState } from "react";
import "altcha";

import { Box, SxProps } from "@mui/material";

import { CaptchaHandle, useCaptcha } from "@hooks/useCaptcha";

const apiUrl = process.env.REACT_APP_API_URL;
const challengeUrl = apiUrl ? `${apiUrl}/captcha/challenge` : undefined;

// Captcha is on when explicitly enabled per-env AND we have an API to fetch the
// challenge from. Self-hosted ALTCHA sets no cookie and calls no third party,
// so it's lawful to run before cookie consent (unlike Google reCAPTCHA).
export const CAPTCHA_ENABLED = process.env.REACT_APP_CAPTCHA_ENABLED === "true" && !!challengeUrl;

// react-snap prerenders with an older bundled Chromium that lacks
// String.prototype.replaceAll (which the ALTCHA widget calls) → it throws during
// prerender. The captcha is useless in static HTML anyway (it must run live in
// the browser), so don't mount the widget during prerender; the client renders
// it on hydration.
const isPrerender = typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

// Reveal the widget if the background solve hasn't finished within this window
// (slow device / stalled) so the user never faces a greyed button with no
// explanation. Happy path solves in well under a second.
const REVEAL_AFTER_MS = 2500;

// Bright panel to match the (white) cookie-consent banner — it should read as a
// light card on the dark form, not a transparent ghost. ALTCHA renders in light
// DOM and reads these CSS custom properties.
const altchaStyle = {
  width: "100%",
  maxWidth: 320,
  "--altcha-max-width": "320px",
  "--altcha-border-width": "1px",
  "--altcha-border-radius": "8px",
  "--altcha-color-base": "#ffffff",
  "--altcha-color-text": "#2b2620",
  "--altcha-color-border": "#d8c4a4",
  "--altcha-color-border-focus": "#ba905c",
} as CSSProperties;

// When hidden, keep the widget mounted + full-size but off-screen so the
// proof-of-work still runs (the solver is JS, not layout-bound) while it takes
// zero space in the form and shows nothing.
const offscreenSx: SxProps = {
  position: "absolute",
  top: 0,
  left: 0,
  width: 320,
  opacity: 0,
  pointerEvents: "none",
  zIndex: -1,
};

type TCaptchaProps = {
  sx?: SxProps;
  // Fires when the widget solves (payload) or resets/expires (null). Lets a host
  // form react — e.g. enable its submit button — without polling.
  onChange?: (token: string | null) => void;
  // Fires once the widget has loaded. Height-animated modals use it to re-measure
  // after the (async) widget appears.
  asyncScriptOnLoad?: () => void;
  // When true, keep the widget invisible while it auto-solves in the background
  // and only reveal it if it errors or is slow — since a passing check needs no
  // interaction, there's nothing to show on the happy path.
  hideUntilNeeded?: boolean;
};

export const Captcha: FC<TCaptchaProps> = ({ sx, onChange, asyncScriptOnLoad, hideUntilNeeded }) => {
  const { captchaRef } = useCaptcha();
  const widgetRef = useRef<HTMLElement | null>(null);
  const solution = useRef<string | null>(null);
  // Revealed = the widget is shown. When hideUntilNeeded, it starts hidden and
  // flips true only on error / slow-solve. When not hideUntilNeeded, always shown.
  const [revealed, setRevealed] = useState(false);
  // Keep the latest callbacks in refs so the listener effect never re-subscribes
  // (host forms pass inline closures for these).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const asyncLoadRef = useRef(asyncScriptOnLoad);
  asyncLoadRef.current = asyncScriptOnLoad;

  useEffect(() => {
    const el = widgetRef.current;
    if (!el) return;

    const readValue = () => (el.querySelector('input[name="altcha"]') as HTMLInputElement | null)?.value || null;

    // Expose the same { getValue, reset } surface the old ReCAPTCHA ref had so
    // host forms (getValue()/reset()) keep working unchanged.
    const handle: CaptchaHandle = {
      getValue: () => solution.current,
      reset: () => {
        solution.current = null;
        (el as unknown as { reset?: () => void }).reset?.();
      },
    };
    captchaRef.current = handle;

    // Slow-solve fallback: if it hasn't solved in time, reveal so the user sees
    // the "verifying" widget rather than a mysteriously-disabled button.
    const revealTimer = hideUntilNeeded
      ? window.setTimeout(() => {
          if (!solution.current) setRevealed(true);
        }, REVEAL_AFTER_MS)
      : undefined;

    const onState = (e: Event) => {
      const state = (e as CustomEvent<{ state?: string }>).detail?.state;
      if (state === "verified") {
        solution.current = readValue();
        onChangeRef.current?.(solution.current);
        // Solved → re-hide (only matters if we'd revealed it after a slow solve).
        if (hideUntilNeeded) setRevealed(false);
      } else if (state === "unverified" || state === "expired" || state === "error") {
        solution.current = null;
        onChangeRef.current?.(null);
        // A real problem → show it so the user can see/retry.
        if (hideUntilNeeded && (state === "expired" || state === "error")) setRevealed(true);
      }
    };
    const onLoad = () => asyncLoadRef.current?.();

    el.addEventListener("statechange", onState);
    el.addEventListener("load", onLoad);
    return () => {
      if (revealTimer) window.clearTimeout(revealTimer);
      el.removeEventListener("statechange", onState);
      el.removeEventListener("load", onLoad);
      if (captchaRef.current === handle) captchaRef.current = null;
    };
  }, [captchaRef, hideUntilNeeded]);

  if (!CAPTCHA_ENABLED || !challengeUrl || isPrerender) {
    return null;
  }

  const shown = !hideUntilNeeded || revealed;

  return (
    <Box
      sx={
        shown
          ? {
              position: "relative",
              zIndex: 1,
              display: "flex",
              justifyContent: "center",
              // Vertical spacing tuned for the standard "captcha then submit" layout
              // across /contact, /license-request, /submit, /newsletter, /partner-program.
              // Bigger top margin pushes the captcha down so it doesn't crowd the
              // last form field; tighter bottom pulls the submit button up close.
              // Phone (<600px) uses a tighter top gap — 40px reads as too much there.
              marginTop: { xs: "24px", sm: "40px" },
              marginBottom: "-24px",
              ...sx,
            }
          : offscreenSx
      }
    >
      {/* auto="onload": solve the proof-of-work in the background as soon as the
          widget mounts — no manual click. altcha 3.x reads the challenge-fetch
          URL from the `challenge` attribute. */}
      <altcha-widget ref={widgetRef} challenge={challengeUrl} auto="onload" style={altchaStyle} />
    </Box>
  );
};
